import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import WorkflowEditorWithProvider from "../../workflow/WorkflowEditor";
import {
  selectActiveCompany,
  selectNodeConfigs,
} from "../../../../core/data/redux/automations/selector";
import {
  fetchAllNodeConfigs,
  fetchNodeDynamicData,
  fetchWorkflowById,
} from "../../../../core/data/redux/automations/thunks";
import { resetWorkspaceState } from "../../../../core/data/redux/automations/slice";
import { notify } from "../../../../services/toast.config";
import Loader from "../../../../core/common/loader";

const AutomationEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const workflowId = id;
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  const activeCompany = useSelector(selectActiveCompany);
  const nodeConfigs = useSelector(selectNodeConfigs);

  // Persist location.state data in component state to prevent loss on re-render
  const [workflowCategory, setWorkflowCategory] = useState<string | null>(
    location?.state?.workflowCategory || null
  );
  const [module, setModule] = useState<string | null>(
    location?.state?.module || null
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<any>(null);

  const backRoute = module ? `/automation/${module}` : "/automation";

  // Capture data from location.state once and persist it
  useEffect(() => {
    if (location?.state?.workflowCategory && !workflowCategory) {
      setWorkflowCategory(location.state.workflowCategory);
    }
    if (location?.state?.module && !module) {
      setModule(location.state.module);
    }
  }, [
    location?.state?.workflowCategory,
    location?.state?.module,
    workflowCategory,
    module,
  ]);

  useEffect(() => {
    dispatch(resetWorkspaceState());
    return () => dispatch(resetWorkspaceState());
  }, [dispatch]);

  useEffect(() => {
    if (!activeCompany?.company_id) {
      setError("Please select a company first");
      return;
    }
    if (!workflowId) {
      setError("Invalid workflow ID");
      return;
    }
    if (!workflowCategory) {
      setError("Workflow category is required");
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setWorkflow(null);

      try {
        await dispatch(
          fetchAllNodeConfigs({
            companyId: activeCompany.company_id.toString(),
            companyType: activeCompany.company_type,
            workflowType: workflowCategory,
          })
        ).unwrap();

        const result = await dispatch(
          fetchWorkflowById({
            companyId: activeCompany.company_id.toString(),
            companyType: activeCompany.company_type,
            workflowId: parseInt(workflowId),
          })
        ).unwrap();

        if (!result || !Array.isArray(result.nodes)) {
          setError("Invalid workflow structure");
          notify.error("Invalid workflow structure");
          return;
        }

        const uniqueNodeKeys = Array.from(
          new Set(
            result.nodes.map((node: any) => node.data?.nodeKey).filter(Boolean)
          )
        );

        await Promise.allSettled(
          uniqueNodeKeys.map((nodeKey: any) =>
            dispatch(
              fetchNodeDynamicData({
                companyId: activeCompany.company_id.toString(),
                companyType: activeCompany.company_type,
                nodeKey,
              })
            ).unwrap()
          )
        );

        setWorkflow(result);
        notify.success(`Workflow "${result.name}" loaded successfully`);
      } catch (err: any) {
        const errorMsg = err?.message || "Failed to load workflow";
        setError(errorMsg);
        notify.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    dispatch,
    activeCompany?.company_id,
    activeCompany?.company_type,
    workflowId,
    workflowCategory,
  ]);

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Workflow</h4>
            <p>{error}</p>
            <hr />
            <div className="d-flex gap-2">
              <button
                onClick={() => navigate(backRoute)}
                className="btn btn-primary">
                <i className="ti ti-arrow-left me-2"></i>
                Back to Workflows
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-secondary">
                <i className="ti ti-reload me-2"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading && <Loader />}
      <div className="page-wrapper">
        <div className="content p-0">
          {!loading && nodeConfigs && workflow ? (
            <WorkflowEditorWithProvider
              nodeConfigs={nodeConfigs}
              workflowType={workflowCategory}
              initialWorkflow={workflow}
            />
          ) : (
            !loading && (
              <div className="empty-state text-center py-5">
                <i className="ti ti-alert-circle fs-1 text-warning"></i>
                <h3 className="mt-3">Workflow Not Found</h3>
                <p className="text-muted">
                  The requested workflow could not be loaded.
                </p>
                <button
                  onClick={() => navigate(backRoute)}
                  className="btn btn-primary mt-3">
                  <i className="ti ti-arrow-left me-2"></i>
                  Back to Workflows
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default AutomationEdit;
